import React, { useState, useEffect, useCallback } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import Header from '../Header';
import SiteFooter from '../SiteFooter';
import '../wizard/Step1_1EmailInput.css';
import '../Wizard.css';
import './LoginPage.css';

const LoginPage: React.FC = () => {
  const {
    email: authEmail,
    isAuthenticated,
    isAuthReady,
    isGeneratingOTP,
    isVerifyingOTP,
    otpSent,
    authError,
    sessionExpired,
    setEmail,
    sendOTP,
    verifyOTPCode,
    resendOTP,
  } = useAuth();
  const navigate = useNavigate();

  const [email, setLocalEmail] = useState(authEmail);
  const [emailError, setEmailError] = useState('');
  const [otp, setOtp] = useState('');
  const [otpError, setOtpError] = useState('');

  const updateHeaderHeight = useCallback(() => {
    const header = document.querySelector('header');
    if (header) {
      document.documentElement.style.setProperty('--header-height', `${header.offsetHeight}px`);
    }
  }, []);

  useEffect(() => {
    const t = setTimeout(updateHeaderHeight, 100);
    window.addEventListener('resize', updateHeaderHeight);
    return () => {
      clearTimeout(t);
      window.removeEventListener('resize', updateHeaderHeight);
    };
  }, [updateHeaderHeight]);

  useEffect(() => {
    if (authEmail) setLocalEmail(authEmail);
  }, [authEmail]);

  // An expired session: send the new code at once, to the email already known.
  const autoSentRef = React.useRef(false);
  useEffect(() => {
    if (!sessionExpired || !authEmail || otpSent || isGeneratingOTP || autoSentRef.current) return;
    autoSentRef.current = true;
    void sendOTP(authEmail);
  }, [sessionExpired, authEmail, otpSent, isGeneratingOTP, sendOTP]);

  if (!isAuthReady) {
    return (
      <div className="login-page">
        <Header subtitle="Sign in" />
        <main className="login-main">
          <div className="loading">Loading…</div>
        </main>
      </div>
    );
  }

  if (isAuthenticated) {
    return <Navigate to="/" replace />;
  }

  const validateEmail = (value: string): boolean => {
    if (!value.trim()) {
      setEmailError('Email is required');
      return false;
    }
    const emailRegex =
      /^[a-zA-Z0-9]([a-zA-Z0-9._-]*[a-zA-Z0-9])?@[a-zA-Z0-9]([a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(\.[a-zA-Z0-9]([a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/;
    if (!emailRegex.test(value.trim())) {
      setEmailError('Please enter a valid email address');
      return false;
    }
    setEmailError('');
    return true;
  };

  const handleGenerateOTP = async () => {
    if (!validateEmail(email)) return;
    setEmail(email.trim());
    await sendOTP(email.trim());
  };

  const handleVerifyOTP = async () => {
    if (!/^\d{6}$/.test(otp.trim())) {
      setOtpError('OTP must be a 6-digit number');
      return;
    }
    setOtpError('');
    const ok = await verifyOTPCode(otp.trim());
    if (ok) {
      navigate('/', { replace: true });
    } else {
      setOtp('');
    }
  };

  return (
    <div className="login-page">
      <Header subtitle="Sign in" />
      <main className="login-main">
        <div className="login-card wizard-step step1-1-email-input">
          <h2 className="wizard-step-title">Sign In / Sign Up</h2>
          {sessionExpired && (
            <div className="login-session-expired" role="status">
              Your session has expired.{' '}
              {otpSent ? <>A new verification code was sent to <strong>{authEmail}</strong>.</> : isGeneratingOTP ? 'Sending a new verification code…' : 'Enter your email to receive a new code.'}
            </div>
          )}
          <p className="wizard-step-description">
            Enter your email address to receive a verification code. This authenticates you
            as a Trustline client and unlocks your dashboard.
          </p>

          {!otpSent && (
            <>
              <div className="wizard-form-group">
                <label htmlFor="login-email">Email Address *</label>
                <input
                  id="login-email"
                  type="email"
                  value={email}
                  onChange={(e) => {
                    setLocalEmail(e.target.value);
                    if (emailError) setEmailError('');
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && !isGeneratingOTP) handleGenerateOTP();
                  }}
                  placeholder="your.email@example.com"
                  className={emailError || authError ? 'error' : ''}
                  disabled={isGeneratingOTP}
                  required
                />
                {emailError && <div className="wizard-error">{emailError}</div>}
                {authError && !emailError && !otpSent && (
                  <div className="wizard-error">{authError}</div>
                )}
              </div>

              {isGeneratingOTP ? (
                <div className="step1-1-loading">
                  <div className="wizard-loading">Sending verification code...</div>
                </div>
              ) : (
                <button
                  type="button"
                  onClick={handleGenerateOTP}
                  disabled={!email.trim()}
                  className="step1-1-generate-btn"
                >
                  Send Verification Code
                </button>
              )}
            </>
          )}

          {otpSent && (
            <>
              <div className="step1-1-otp-success-notice">
                <div className="step1-1-success-icon-small">✓</div>
                <p className="step1-1-otp-notice-message">
                  Verification code sent to <strong>{authEmail || email}</strong>
                </p>
              </div>

              <div className="step1-1-otp-section">
                <div className="wizard-form-group">
                  <label htmlFor="login-otp">Verification Code *</label>
                  <input
                    id="login-otp"
                    type="text"
                    inputMode="numeric"
                    pattern="[0-9]*"
                    value={otp}
                    onChange={(e) => {
                      setOtp(e.target.value.replace(/\D/g, '').slice(0, 6));
                      if (otpError) setOtpError('');
                    }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && !isVerifyingOTP && otp.length === 6) {
                        handleVerifyOTP();
                      }
                    }}
                    placeholder="000000"
                    className={`step1-1-otp-input ${otpError || authError ? 'error' : ''}`}
                    disabled={isVerifyingOTP}
                    maxLength={6}
                    required
                  />
                  {otpError && <div className="wizard-error">{otpError}</div>}
                  {authError && !otpError && (
                    <div className="wizard-error">{authError}</div>
                  )}
                  <small className="step1-1-otp-hint">
                    Enter the 6-digit code from your email. The code expires after 5 minutes.
                  </small>
                </div>

                {isVerifyingOTP ? (
                  <div className="step1-1-loading">
                    <div className="wizard-loading">Verifying code...</div>
                  </div>
                ) : (
                  <div className="step1-1-otp-actions">
                    <button
                      type="button"
                      onClick={handleVerifyOTP}
                      disabled={otp.length !== 6}
                      className="step1-1-verify-btn"
                    >
                      Verify Code
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        resendOTP();
                        setOtp('');
                        setOtpError('');
                      }}
                      className="step1-1-resend-btn"
                    >
                      Resend Code
                    </button>
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      </main>
      <SiteFooter />
    </div>
  );
};

export default LoginPage;
